function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
var _summaryThreshold = _classPrivateFieldLooseKey("summaryThreshold");
var _historyAbortController = _classPrivateFieldLooseKey("historyAbortController");
var _handleHistoryOpen = _classPrivateFieldLooseKey("handleHistoryOpen");
var _handleHistoryClose = _classPrivateFieldLooseKey("handleHistoryClose");
var _handlePopState = _classPrivateFieldLooseKey("handlePopState");
var _handleCartAdd = _classPrivateFieldLooseKey("handleCartAdd");
var _updateStickyState = _classPrivateFieldLooseKey("updateStickyState");
class CartDrawerComponent extends DialogComponent {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _updateStickyState, {
      value: _updateStickyState2
    });
    /** @type {number} */
    Object.defineProperty(this, _summaryThreshold, {
      writable: true,
      value: 0.5
    });
    /** @type {AbortController | null} */
    Object.defineProperty(this, _historyAbortController, {
      writable: true,
      value: null
    });
    Object.defineProperty(this, _handleHistoryOpen, {
      writable: true,
      value: () => {
        if (!isMobileBreakpoint()) return;
        if (!history.state?.cartDrawerOpen) {
          history.pushState({
            cartDrawerOpen: true
          }, '');
        }
        _classPrivateFieldLooseBase(this, _historyAbortController)[_historyAbortController] = new AbortController();
        window.addEventListener('popstate', _classPrivateFieldLooseBase(this, _handlePopState)[_handlePopState], {
          signal: _classPrivateFieldLooseBase(this, _historyAbortController)[_historyAbortController].signal
        });
      }
    });
    Object.defineProperty(this, _handleHistoryClose, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _historyAbortController)[_historyAbortController]?.abort();
        if (history.state?.cartDrawerOpen) {
          history.back();
        }
      }
    });
    Object.defineProperty(this, _handlePopState, {
      writable: true,
      value: async () => {
        if (this.refs.dialog?.open) {
          this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
          await this.closeDialog();
          this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
        }
      }
    });
    Object.defineProperty(this, _handleCartAdd, {
      writable: true,
      value: (/** @type {CustomEvent} */event) => {
        if (event.detail?.data?.skipCartDrawer) return;
        if (this.hasAttribute('auto-open')) {
          this.showDialog();
        }
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, _classPrivateFieldLooseBase(this, _handleCartAdd)[_handleCartAdd]);
    this.addEventListener(DialogOpenEvent.eventName, _classPrivateFieldLooseBase(this, _updateStickyState)[_updateStickyState]);
    this.addEventListener(DialogOpenEvent.eventName, _classPrivateFieldLooseBase(this, _handleHistoryOpen)[_handleHistoryOpen]);
    this.addEventListener(DialogCloseEvent.eventName, _classPrivateFieldLooseBase(this, _handleHistoryClose)[_handleHistoryClose]);
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, _classPrivateFieldLooseBase(this, _handleCartAdd)[_handleCartAdd]);
    this.removeEventListener(DialogOpenEvent.eventName, _classPrivateFieldLooseBase(this, _updateStickyState)[_updateStickyState]);
    this.removeEventListener(DialogOpenEvent.eventName, _classPrivateFieldLooseBase(this, _handleHistoryOpen)[_handleHistoryOpen]);
    this.removeEventListener(DialogCloseEvent.eventName, _classPrivateFieldLooseBase(this, _handleHistoryClose)[_handleHistoryClose]);
    _classPrivateFieldLooseBase(this, _historyAbortController)[_historyAbortController]?.abort();
  }
  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, {
        once: true
      });
    });
  }
  close() {
    this.closeDialog();
  }
}
function _updateStickyState2() {
  const {
    dialog
  } = /** @type {Refs} */this.refs;
  if (!dialog) return;

  // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
  const content = dialog.querySelector('.cart-drawer__content');
  const summary = dialog.querySelector('.cart-drawer__summary');
  if (!content || !summary) {
    // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
    dialog.setAttribute('cart-summary-sticky', 'false');
    return;
  }
  const drawerHeight = dialog.getBoundingClientRect().height;
  const summaryHeight = summary.getBoundingClientRect().height;
  const ratio = summaryHeight / drawerHeight;
  dialog.setAttribute('cart-summary-sticky', ratio > _classPrivateFieldLooseBase(this, _summaryThreshold)[_summaryThreshold] ? 'false' : 'true');
}
if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
